import { hasUrlFeatureFlagTrain } from '../../components/molecules/URLFeatureFlag/URLFeatureFlag'
import config, { Environment } from '../../config/config'
import { Attempt, Institution, UserProfile } from '../../models/global.types'

export type CertificateVariant = 'enterprise' | 'd2c'
export type CertificateYear = 2025 | 2026

export const CERT_2026_CUTOFF_MS = Date.parse('2026-01-01T00:00:00.000Z')

export const OVERLAY_NAVY: [number, number, number] = [15, 52, 67]
export const OVERLAY_DARK_GRAY: [number, number, number] = [16, 17, 17]
export const NAME_MINT_SHADOW: [number, number, number] = [153, 243, 207]

// Letterbox page in jsPDF px units, rotated to landscape by generateCertificatePdf.
export const PDF_PAGE_SHORT_SIDE = 612
export const PDF_PAGE_LONG_SIDE = 792

export const PDF_NAME_FONT_SIZE = 48
export const PDF_TEXT_FONT_SIZE = 28
export const PDF_INSTITUTION_FONT_SIZE = 22
export const PDF_NAME_SHADOW_OFFSET = 2

export const CERT_BACKGROUND_URLS = {
  enterprise: {
    2025: '/assets/images/Completion_Cert_BLANK.jpg',
    2026: '/assets/images/Completion_Cert_BLANK_2026.jpg'
  },
  d2c: {
    2025: '/assets/images/Completion_Cert_BLANK_D2C.jpg',
    2026: '/assets/images/Completion_Cert_BLANK_D2C_2026.jpg'
  }
} as const

export const DEV_CERTIFICATE_DEFAULTS = {
  firstName: 'Jane',
  lastName: 'Rescuer',
  institutionName: 'Whittier College',
  referenceNumber: 'DEV-0001',
  variant: 'enterprise' as CertificateVariant
}

export const DEV_SAMPLE_DATE_2025 = '2025-12-15'
export const DEV_SAMPLE_DATE_2026 = '2026-01-15'

export const isCertificateDevToolEnabled = (): boolean =>
  config.environment === Environment.DEVELOPMENT || config.environment === Environment.STAGE

export const isCertificateDevToolAuthRequired = (): boolean =>
  config.environment === Environment.STAGE

export interface CertificateInput {
  firstName: string
  lastName: string
  institutionName?: string
  completedAt: Date
  referenceNumber: string
  variant: CertificateVariant
}

export interface CertificateFields {
  firstName?: string
  lastName?: string
  institutionName?: string
  endedAt?: Date | string
  referenceNumber?: string
  variant?: CertificateVariant
}

export interface OverlayPoint {
  xPct: number
  yPct: number
}

export interface CertificateOverlayLayout {
  name: OverlayPoint
  institution: OverlayPoint
  date: OverlayPoint
  reference: OverlayPoint
}

// jsPDF anchors text on its baseline while the HTML preview anchors on the vertical
// center of the line. This is the baseline drop from that center as a fraction of
// font size — roughly half the cap height of Helvetica.
const BASELINE_DROP_PER_FONT_SIZE = 0.32

// yPct is the vertical center of each field, so preview and PDF agree. The 2025
// values are set so the PDF baselines come out where the 2025 certificate has
// always drawn them (name 190, institution 292, date and reference 400).
const LAYOUT_2025: CertificateOverlayLayout = {
  name: { xPct: 50, yPct: 28.54 },
  institution: { xPct: 50, yPct: 46.56 },
  date: { xPct: 31.7, yPct: 63.9 },
  reference: { xPct: 69.9, yPct: 63.9 }
}

// The 2026 artwork bakes in a header and body copy, so every field sits lower.
// x values are the centers of the two signature rules.
const LAYOUT_2026: CertificateOverlayLayout = {
  name: { xPct: 50, yPct: 33 },
  institution: { xPct: 50, yPct: 51.5 },
  date: { xPct: 31.7, yPct: 71.3 },
  reference: { xPct: 69.9, yPct: 71.3 }
}

export const getCertificateOverlayLayout = (year: CertificateYear): CertificateOverlayLayout =>
  year === 2026 ? LAYOUT_2026 : LAYOUT_2025

export const overlayPctToPdf = (pct: number, pageSize: number, fontSize = 0): number =>
  pct / 100 * pageSize + fontSize * BASELINE_DROP_PER_FONT_SIZE

export const getCertificateYear = (completedAt: Date): CertificateYear =>
  completedAt.getTime() >= CERT_2026_CUTOFF_MS ? 2026 : 2025

export const getOverlayColor = (year: CertificateYear): [number, number, number] =>
  year === 2026 ? OVERLAY_DARK_GRAY : OVERLAY_NAVY

export const overlayColorToCss = (color: [number, number, number]): string =>
  `rgb(${color[0]}, ${color[1]}, ${color[2]})`

export const getCertificateBackgroundUrl = (
  variant: CertificateVariant,
  year: CertificateYear
): string => CERT_BACKGROUND_URLS[variant][year]

export const getCertificateVariant = (): CertificateVariant =>
  hasUrlFeatureFlagTrain() ? 'd2c' : 'enterprise'

/**
 * Rendered in the viewer's local timezone, which is how every certificate issued so
 * far was drawn — re-downloading an old certificate must not change its printed date.
 * Choosing the 2026 artwork is a separate decision and compares the UTC instant.
 */
export const formatCertificateDate = (completedAt: Date): string =>
  completedAt.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })

export const buildCertificateInputFromFields = (fields: CertificateFields): CertificateInput | null => {
  const { firstName, lastName, institutionName, endedAt, referenceNumber } = fields
  const variant = fields.variant ?? getCertificateVariant()

  if (firstName == null || lastName == null ||
      endedAt == null || referenceNumber == null || referenceNumber.length === 0) {
    return null
  }

  // Only the enterprise certificate draws an institution, so a D2C student does not
  // have to wait on the account fetch before their certificate can be built.
  if (variant === 'enterprise' && institutionName == null) {
    return null
  }

  return {
    firstName,
    lastName,
    institutionName,
    completedAt: new Date(endedAt),
    referenceNumber,
    variant
  }
}

export const buildCertificateInput = (
  userProfile: UserProfile | null,
  institution: Institution | null,
  currentAttempt: Attempt | null,
  variantOverride?: CertificateVariant
): CertificateInput | null => buildCertificateInputFromFields({
  firstName: userProfile?.first_name,
  lastName: userProfile?.last_name,
  institutionName: institution?.name,
  endedAt: currentAttempt?.endedAt,
  referenceNumber: currentAttempt?.referenceNumber,
  variant: variantOverride
})

/** Identifies the certificate an input produces, so a cached PDF is only reused for the same one. */
export const certificateInputKey = (input: CertificateInput): string => [
  input.firstName,
  input.lastName,
  input.institutionName ?? '',
  input.completedAt.getTime(),
  input.referenceNumber,
  input.variant
].join('|')
